import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SubjectService } from 'src/app/services/api/subjects/subject.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { SubjectModel, SubjectPayLoad } from 'src/app/services/models/subjects/subject.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { SharedModule } from 'src/shared/shared.module';
import { PipesModule } from 'src/utils/pipes/pipes.module';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};

export type SubjectSaveAction = 'create' | 'edit';

export interface SubjectSaveFailedEvent {
  action: SubjectSaveAction;
  message: string;
}

@Component({
  selector: 'app-form-add-subject',
  imports: [CommonModule, TranslateModule, SharedModule, PipesModule, ReactiveFormsModule],
  templateUrl: './form-add-subject.component.html',
  styleUrl: './form-add-subject.component.scss',
})
export class FormAddSubjectComponent implements AfterViewInit {
  @ViewChild('saveSubjectConfirmDialog')
  saveSubjectConfirmDialog!: ConfirmDialogComponent;

  @Output() saved = new EventEmitter<SubjectSaveAction>();
  @Output() saveFailed = new EventEmitter<SubjectSaveFailedEvent>();
  @Output() closed = new EventEmitter<void>();

  fb = inject(FormBuilder);
  subjectService = inject(SubjectService);
  studyPeriodService = inject(StudyPeriodService);

  isSaving = false;
  isSynced = false;
  canDelete = true;
  usedInOfferings = false;
  lastEditedByName: string | null = null;
  lastEditedAt: string | null = null;
  academicTerms: AcademicTermYearModel[] = [];
  yearOptions: string[] = [];
  termOptions: AcademicTermYearModel[] = [];
  subjectId?: number;

  form = this.fb.group({
    academicTermName: [null as string | null, Validators.required],
    academicTermYear: [null as string | null, Validators.required],
    code: ['', Validators.required],
    name: ['', Validators.required],
  });

  private isPatchingForm = false;

  get isEditMode(): boolean {
    return this.subjectId != null;
  }

  get isTermDisabled(): boolean {
    return this.isEditMode;
  }

  get isRestrictedEdit(): boolean {
    return this.isEditMode && (this.isSynced || !this.canDelete || this.usedInOfferings);
  }

  get isCodeDisabled(): boolean {
    return this.isRestrictedEdit;
  }

  get showAuditInfo(): boolean {
    return this.isEditMode && this.lastEditedAt != null;
  }

  ngAfterViewInit(): void {
    const modalEl = document.getElementById('addNewSubject');

    modalEl?.addEventListener('hidden.bs.modal', () => {
      this.clearForm();
      this.closed.emit();
    });
  }

  openModal(subjectId?: number): void {
    this.clearForm();
    this.subjectId = subjectId;

    if (subjectId == null) {
      this.loadAcademicTermYears();
    } else {
      this.loadSubjectDetail(subjectId);
    }

    const modalEl = document.getElementById('addNewSubject');

    if (modalEl == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalEl, STATIC_MODAL_OPTIONS).show();
  }

  onYearChange(year: string | null): void {
    this.termOptions = this.academicTerms.filter(
      (term) => String(this.toBuddhistYear(term.year)) === year,
    );

    if (this.isPatchingForm) {
      return;
    }

    this.form.patchValue({ academicTermName: null });
  }

  onSave(): void {
    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saveSubjectConfirmDialog.open();
  }

  onConfirmSave(): void {
    if (this.isSaving) {
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.saveSubjectConfirmDialog.close();
      return;
    }

    const action: SubjectSaveAction = this.subjectId == null ? 'create' : 'edit';

    this.isSaving = true;

    this.getSaveRequest(this.buildPayload()).subscribe({
      next: () => {
        this.saveSubjectConfirmDialog.close();
        this.saved.emit(action);
        this.closeModal();
      },
      error: (err) => {
        console.error(err);
        this.saveSubjectConfirmDialog.close();
        this.saveFailed.emit({
          action,
          message: this.getApiErrorMessage(err, this.getSaveFallbackMessage(action)),
        });
        this.isSaving = false;
      },
      complete: () => {
        this.isSaving = false;
      },
    });
  }

  clearForm(): void {
    this.subjectId = undefined;
    this.isSynced = false;
    this.canDelete = true;
    this.usedInOfferings = false;
    this.lastEditedByName = null;
    this.lastEditedAt = null;
    this.form.reset({
      academicTermName: null,
      academicTermYear: null,
      code: '',
      name: '',
    });
    this.termOptions = [];
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private loadAcademicTermYears(): void {
    this.studyPeriodService.getAcademicTermYears().subscribe({
      next: (terms) => {
        this.academicTerms = terms;
        this.yearOptions = Array.from(
          new Set(terms.map((term) => String(this.toBuddhistYear(term.year)))),
        ).sort((a, b) => a.localeCompare(b));
      },
      error: (err) => console.error(err),
    });
  }

  private loadSubjectDetail(subjectId: number): void {
    this.subjectService.getSubjectById(subjectId).subscribe({
      next: (subject) => this.patchFormValue(subject),
      error: (err) => console.error(err),
    });
  }

  private patchFormValue(subject: SubjectModel): void {
    const academicTerm = subject.academic_term;
    const year = academicTerm == null
      ? ''
      : String(this.toBuddhistYear(academicTerm.year));
    const termName = academicTerm?.name ?? '';

    this.isSynced = subject.is_synced;
    this.canDelete = subject.can_delete;
    this.usedInOfferings = subject.used_in_offerings ?? false;
    this.lastEditedByName = subject.updated_by?.full_name ?? null;
    this.lastEditedAt = subject.updated_at ?? null;
    this.academicTerms = academicTerm == null ? [] : [academicTerm];
    this.yearOptions = year === '' ? [] : [year];
    this.termOptions = this.academicTerms;
    this.isPatchingForm = true;
    this.form.patchValue({
      academicTermName: termName,
      academicTermYear: year,
      code: subject.code,
      name: subject.name,
    });
    this.isPatchingForm = false;
  }

  private getSaveRequest(payload: SubjectPayLoad) {
    if (this.subjectId == null) {
      return this.subjectService.createSubject(payload);
    }

    return this.subjectService.updateSubject(this.subjectId, payload);
  }

  private buildPayload(): SubjectPayLoad {
    const value = this.form.getRawValue();

    if (this.isRestrictedEdit) {
      return {
        subject: {
          name: value.name ?? '',
        },
      };
    }

    if (this.isEditMode) {
      return {
        subject: {
          code: value.code ?? '',
          name: value.name ?? '',
        },
      };
    }

    return {
      subject: {
        code: value.code ?? '',
        name: value.name ?? '',
        academic_term_name: value.academicTermName ?? '',
        academic_term_year: this.toChristianYear(value.academicTermYear),
      },
    };
  }

  private toChristianYear(value: unknown): number {
    const year = Number(value);

    return year > 2400 ? year - 543 : year;
  }

  private toBuddhistYear(year: number): number {
    return year < 2400 ? year + 543 : year;
  }

  private getSaveFallbackMessage(action: SubjectSaveAction): string {
    return action === 'create'
      ? 'ไม่สามารถเพิ่มรายการได้ กรุณาลองใหม่อีกครั้ง'
      : 'ไม่สามารถแก้ไขรายการได้ กรุณาลองใหม่อีกครั้ง';
  }

  private getApiErrorMessage(error: unknown, fallbackMessage: string): string {
    const normalizedError = this.normalizeErrorBody(error);

    if (typeof normalizedError === 'string' && normalizedError.trim() !== '') {
      return normalizedError;
    }

    if (normalizedError !== null && typeof normalizedError === 'object') {
      const errorRecord = normalizedError as Record<string, unknown>;
      const errors = errorRecord['errors'];
      const errorMessage = errorRecord['error'];
      const message = errorRecord['message'];

      if (Array.isArray(errors) && errors.length > 0) {
        return errors.map((item) => String(item)).join('\n');
      }

      if (typeof errors === 'string' && errors.trim() !== '') {
        return errors;
      }

      if (typeof errorMessage === 'string' && errorMessage.trim() !== '') {
        return errorMessage;
      }

      if (typeof message === 'string' && message.trim() !== '') {
        return message;
      }
    }

    return fallbackMessage;
  }

  private normalizeErrorBody(error: unknown): unknown {
    if (error !== null && typeof error === 'object' && 'error' in error) {
      return this.normalizeErrorBody((error as { error?: unknown }).error);
    }

    if (typeof error !== 'string') {
      return error;
    }

    try {
      return JSON.parse(error);
    } catch {
      return error;
    }
  }

  private closeModal(): void {
    const modalElement = document.getElementById('addNewSubject');

    if (modalElement == null) {
      return;
    }

    const modal = bootstrap.Modal.getInstance(modalElement);
    modal?.hide();
  }
}
